import { useMemo } from "react";
import { useSelector } from "react-redux";
import {
  HotelStarRating,
  HotelTripadvisorRating,
  LocationWithMap,
  RoomFeatures,
  RoomInfo,
  useRoomProductDetails,
  CheckInPoliciesSection,
} from "@apac/components/hotels";
import { useHotelGuestCount } from "@apac/hooks";
import { HotelsShopSelectors } from "@b2bportal/core-hotels";
import { IconNameEnum, useModuleBEM } from "@b2bportal/core-themes";
import type { PlatformComponentProps } from "@b2bportal/core-types";
import { CheckInTimeEnum } from "@b2bportal/lodging-api";
import { DateTimeFormatStyle, useI18nContext } from "@hopper-b2b/i18n";
import clsx from "clsx";
import dayjs from "dayjs";
import styles from "./styles.module.scss";

export const HotelDetailsReviewSection = ({
  className,
  showHotelInfo,
  showCheckinInstruction,
}: PlatformComponentProps & {
  showHotelInfo?: boolean;
  showCheckinInstruction?: boolean;
}) => {
  const [block, cn] = useModuleBEM(styles, "HotelDetailsReviewSection");
  const { t, formatDateTime } = useI18nContext();

  const hotel = useSelector(HotelsShopSelectors.getSelectedHotel);
  const checkInPolicies = useSelector(HotelsShopSelectors.getCheckInPolicies);
  const checkInInformation = useSelector(
    HotelsShopSelectors.getCheckInInformation
  );
  const { roomInfo } = useSelector(HotelsShopSelectors.getSelectedRoomInfo);
  const roomProduct = useSelector(HotelsShopSelectors.getSelectedRoom);
  const fromDate = useSelector(HotelsShopSelectors.getFromDate);
  const untilDate = useSelector(HotelsShopSelectors.getUntilDate);

  const { title: cancellationPolicyTitle, subtitle } =
    useRoomProductDetails(roomProduct);

  const { checkinTime, checkoutTime } = useMemo(() => {
    const checkInTimeFrom =
      checkInInformation?.checkInTime &&
      checkInInformation?.checkInTime?.CheckInTime !== CheckInTimeEnum.Anytime
        ? checkInInformation?.checkInTime?.checkInTimeFrom
        : "";

    const checkInDate = formatDateTime(
      dayjs(fromDate).toDate(),
      DateTimeFormatStyle.MediumDate
    );
    const checkOutDate = formatDateTime(
      dayjs(untilDate).toDate(),
      DateTimeFormatStyle.MediumDate
    );

    return {
      checkinTime: checkInTimeFrom
        ? `${checkInDate} (${checkInTimeFrom})`
        : checkInDate,
      checkoutTime: checkInInformation?.checkOutTime
        ? `${checkOutDate} (${checkInInformation.checkOutTime})`
        : checkOutDate,
    };
  }, [
    checkInInformation?.checkInTime,
    checkInInformation?.checkOutTime,
    formatDateTime,
    fromDate,
    untilDate,
  ]);

  const { formattedGuestCountString } = useHotelGuestCount();
  const roomsCount = useSelector(HotelsShopSelectors.getRoomsCount);

  return (
    <div className={clsx(block, className)}>
      <div>
        <div className={cn("hotel-info", { border: showHotelInfo })}>
          <div className={cn("hotel-name")}>{hotel.lodging.name}</div>
          {showHotelInfo && (
            <>
              <LocationWithMap
                hotel={hotel}
                icon={IconNameEnum.location}
                triggerClassName={cn("location")}
              />
              <div className={cn("ratings")}>
                <HotelStarRating
                  starRating={hotel?.lodging?.starRating}
                  className={cn("score")}
                />
                <HotelTripadvisorRating hotel={hotel} className={cn("score")} />
              </div>
            </>
          )}
        </div>
        <img
          className={cn("hotel-summary-image")}
          src={hotel.lodging.media?.[0].url}
          alt="hotel summary"
        />
      </div>
      <div className={cn("hotel-details-container")}>
        <div className={cn("room-section")}>
          <RoomInfo {...{ ...roomInfo, amenities: undefined }} />
        </div>

        <div className={cn("features-container")}>
          <RoomFeatures
            cancellationPolicyText={cancellationPolicyTitle}
            mealPlan={roomProduct.mealPlan}
          />
          <span className={cn("cancellation")}>{subtitle}</span>
        </div>
      </div>

      <div className={cn("time-details-info")}>
        <div className={cn("time-line")}>
          <span className={cn("title")}>
            {t("core-hotels.shop.checkInInformation.checkinTitle")}
          </span>
          <div className={cn("time")}>{checkinTime}</div>
        </div>
        <div className={cn("time-line")}>
          <span className={cn("title")}>
            {t("core-hotels.shop.checkInInformation.checkoutTitle")}
          </span>
          <div className={cn("time")}>{checkoutTime}</div>
        </div>
        <div className={cn("time-line")}>
          <span className={cn("title")}>
            {t("passengerInformation.guests")}
          </span>
          <div className={cn("time")}>{formattedGuestCountString()}</div>
        </div>
        <div className={cn("time-line")}>
          <span className={cn("title")}>{t("rooms")}</span>
          <div className={cn("time")}>
            {t("recentSearch.hotels.rooms", { count: roomsCount ?? 1 })}
          </div>
        </div>
      </div>

      {showCheckinInstruction && (
        <CheckInPoliciesSection
          checkInPolicies={checkInPolicies}
          className={cn("checkin-instructions")}
        />
      )}
    </div>
  );
};
